import * as Sentry from '@sentry/react';
import { cloneDeep } from 'lodash';

import localStorageEx from 'bundles/common/utils/localStorageEx';
import { isAALViewOnlyActive } from 'bundles/course-staff-impersonation/utils/actAsLearnerSessionUtils';
import FlexExperiments from 'bundles/epic/clients/Flex';
import getLocalStorageProgressKey from 'bundles/ondemand/utils/progress/utils/getLocalStorageProgressKey';

type CourseProgressItem = {
  progressState: string;
  timestamp: number;
};

type CachedCourseProgressItem = {
  progressState: string;
  timestamp?: number;
};

type CourseProgress = {
  progressMapping: Record<string, { get: (key: string) => string }>;
};

type LocalStorageProgress = {
  [itemId: string]: CachedCourseProgressItem;
};

type RawProgressElement = {
  items: Record<string, CourseProgressItem>;
};

type RawProgress = {
  paging: any;
  linked: any;
  elements: RawProgressElement[];
};

// Store in cache so if page is refreshed and the item completed event has not processed yet, it still shows as completed.
export const saveProgressToLocalStorage = (progressId: string, courseProgress: CourseProgress) => {
  if (window?.localStorage && FlexExperiments.get('useLocalStorageProgressOverrides') && !isAALViewOnlyActive()) {
    const localStorageProgressKey = getLocalStorageProgressKey(progressId);
    const itemsProgress = courseProgress.progressMapping;
    const localStorageProgress = JSON.parse(window.localStorage.getItem(localStorageProgressKey) || '{}');
    const currentTime = new Date().toISOString();

    // Remove items that are not in the new format (i.e., do not have a timestamp)
    Object.keys(localStorageProgress).forEach((itemId) => {
      if (!localStorageProgress[itemId].timestamp) {
        delete localStorageProgress[itemId];
      }
    });

    // Add or update items with the current progress state and timestamp
    Object.keys(itemsProgress).forEach((itemId) => {
      const itemProgressState = itemsProgress[itemId].get('progressState');
      if (!localStorageProgress[itemId] && itemProgressState === 'Completed') {
        localStorageProgress[itemId] = {
          progressState: itemProgressState,
          timestamp: currentTime,
        };
      }
    });

    localStorageEx.setItem(localStorageProgressKey, localStorageProgress, JSON.stringify);
  }
};

export const readProgressFromLocalStorage = (courseProgressId: string) => {
  try {
    return localStorageEx.getItem(getLocalStorageProgressKey(courseProgressId), JSON.parse, undefined);
  } catch (error) {
    Sentry.captureException(error);
    return undefined;
  }
};

export const mergeProgressFromLocalStorage = (
  rawProgress: RawProgress,
  localStorageProgress?: LocalStorageProgress | null
) => {
  if (!localStorageProgress || !rawProgress?.elements || rawProgress.elements.length === 0) {
    return rawProgress;
  }

  const mergedItemProgresses = cloneDeep(rawProgress.elements[0].items);
  Object.keys(localStorageProgress).forEach((itemId) => {
    const localStorageItem = localStorageProgress[itemId];
    // Ignore old format items without timestamp
    if (localStorageItem?.timestamp) {
      if (localStorageItem.progressState === 'Completed') {
        mergedItemProgresses[itemId] = {
          progressState: localStorageItem.progressState,
          timestamp: localStorageItem.timestamp,
        };
      }
    }
  });

  return {
    paging: rawProgress.paging,
    linked: rawProgress.linked,
    elements: [
      {
        ...rawProgress.elements[0],
        items: mergedItemProgresses,
      },
    ],
  };
};

const CACHE_EXPIRATION_TIME = 60 * 1000; // 1 minute
export const invalidateCache = (progressId: string) => {
  if (window?.localStorage) {
    const localStorageProgressKey = getLocalStorageProgressKey(progressId);
    try {
      const localStorageProgress = JSON.parse(window.localStorage.getItem(localStorageProgressKey) ?? '{}');
      const currentTime = new Date().getTime();
      Object.keys(localStorageProgress).forEach((itemId) => {
        const item = localStorageProgress[itemId];
        if (item.timestamp) {
          const itemTime = new Date(item.timestamp).getTime();
          if (currentTime - itemTime > CACHE_EXPIRATION_TIME) {
            delete localStorageProgress[itemId];
          }
        } else {
          // Remove item if it doesn't have a timestamp (old format)
          delete localStorageProgress[itemId];
        }
      });
      localStorageEx.setItem(localStorageProgressKey, localStorageProgress, JSON.stringify);
    } catch (e) {
      Sentry.captureException(e);
      localStorageEx.setItem(localStorageProgressKey, {}, JSON.stringify);
    }
  }
};
